import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { concatLatestFrom } from '@ngrx/effects';
import {
  CommissioningStore,
  DevicesStore,
  InstallationFeaturesStore,
  RemoteDeviceType,
  ZIGBEE_COORDINATOR_STATUS,
} from '@store';
import { Feature, FeatureNames, IsActiveProperty } from '@vi-iot/client';
import { Subject, combineLatest } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import {
  Device,
  DEVICES_TO_COMMISSIONING_FOR_OBNG_STANDALONE_INSTALLATION,
  DEVICES_TO_COMMISSIONING_FOR_STANDALONE_INSTALLATION,
  ModalAction,
} from '../commissioning.model';
import { CommissioningService } from '../commissioning.service';

@Component({
  templateUrl: './commissioning-device-type-modal.component.html',
  styleUrls: ['./commissioning-device-type-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommissioningDeviceTypeModalComponent implements OnInit, OnDestroy {
  devices: Device[];
  selectedDevice: Device;
  deviceZigbeeActive = true;
  ZIGBEE_COORDINATOR_STATUS = ZIGBEE_COORDINATOR_STATUS;

  private ngUnsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    public commissioningService: CommissioningService,
    public commissioningStore: CommissioningStore,
    private devicesStore: DevicesStore,
    private installationFeaturesStore: InstallationFeaturesStore,
    private dialogRef: MatDialogRef<CommissioningDeviceTypeModalComponent>
  ) {}

  ngOnInit(): void {
    this.dialogRef.updateSize('768px');
    this.dialogRef.addPanelClass('commissioning-modal');
    this.commissioningStore.resetCommissioningState();
    this.commissioningStore.listFeatures();

    combineLatest([this.devicesStore.isStandaloneDevice$, this.devicesStore.isSelectedDeviceOBNG$])
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(([isStandaloneDevice, isSelectedDeviceOBNG]) => {
        if (isSelectedDeviceOBNG) {
          this.commissioningService.devicesToCommissioning$.next(
            DEVICES_TO_COMMISSIONING_FOR_OBNG_STANDALONE_INSTALLATION
          );
        } else if (isStandaloneDevice && !isSelectedDeviceOBNG) {
          this.commissioningService.devicesToCommissioning$.next(
            DEVICES_TO_COMMISSIONING_FOR_STANDALONE_INSTALLATION
          );
        }
      });

    this.commissioningService.devicesToCommissioning$
      .pipe(
        concatLatestFrom(() => [
          this.devicesStore.isFloorHeatingThermostatAvailable$,
          this.installationFeaturesStore.canAddVitotrolToInstallation$,
        ]),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe(([devices, isFhtAvailable, canAddVitotrol300]: [Device[], boolean, boolean]) => {
        this.devices = devices;
        if (!isFhtAvailable) {
          this.devices = this.devices.filter((value) => value.type !== RemoteDeviceType.FHT);
        }
        if (!canAddVitotrol300) {
          this.devices = this.devices.filter(
            (value) => value.type !== RemoteDeviceType.VITOTROL_300E
          );
        }
      });

    this.installationFeaturesStore
      .selectInstallationDeviceFeature(FeatureNames.deviceZigbeeActive)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((data: Feature<IsActiveProperty, never>) => {
        if (data?.properties?.active) {
          this.deviceZigbeeActive = data.properties.active.value;
        }
      });

    combineLatest(
      this.commissioningService.currentRemoteDeviceCode$,
      this.commissioningService.pairingStatusName$
    )
      .pipe(
        filter(
          ([_currentRemoteDeviceCode, pairingStatusName]) =>
            pairingStatusName === ZIGBEE_COORDINATOR_STATUS.TYPE_MISMATCH
        ),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe(([code]) => {
        this.commissioningStore.setCode(code);
      });
  }

  onDeviceTypeChange(_device: Device): void {
    this.selectedDevice = _device;
    this.commissioningStore.changeDeviceType(_device.type);
    this.commissioningService.selectedDevice$.next(_device);
    this.commissioningService.updateStrategy(_device.strategy());
  }

  navigateForward(): void {
    this.commissioningStore.changeDeviceType(this.selectedDevice.type);
    this.commissioningService.selectedDevice$.next(this.selectedDevice);

    this.commissioningService.modalAction$.next(ModalAction.NextStep);
  }

  closeDialog(): void {
    this.commissioningService.modalAction$.next(ModalAction.Close);
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
